import { OrganizationPlanBooking } from "api/account/types";

/**
 * Pure helpers for the organization plan page (#3037). Filtering happens on
 * the rows the server already decided this person may see — narrowing a list
 * is presentation, not authorization.
 */

/** The "no filter" option. Radix Select cannot hold an empty-string value. */
export const ALL = "__all__";

export interface FilterOption {
  value: string;
  label: string | null;
}

/** The resource's name in the reader's language, falling back to the other one. */
export const resourceLabel = (
  booking: Pick<OrganizationPlanBooking, "resource">,
  language: string
): string | null => {
  const lang = (language ?? "en").toLowerCase().split("-")[0];
  const { de, en } = booking.resource ?? { de: null, en: null };
  return (lang === "de" ? de ?? en : en ?? de) ?? null;
};

const byLabel = (a: FilterOption, b: FilterOption) =>
  (a.label ?? "").localeCompare(b.label ?? "");

/** One option per distinct resource key, named in the reader's language. */
export const resourceOptions = (
  bookings: OrganizationPlanBooking[],
  language: string
): FilterOption[] => {
  const seen = new Map<string, FilterOption>();
  bookings.forEach((booking) => {
    const key = booking.resource?.key;
    if (key && !seen.has(key)) {
      seen.set(key, { value: key, label: resourceLabel(booking, language) });
    }
  });
  return Array.from(seen.values()).sort(byLabel);
};

/** One option per distinct booker account. A booking without an account has no member to filter by. */
export const bookerOptions = (bookings: OrganizationPlanBooking[]): FilterOption[] => {
  const seen = new Map<string, FilterOption>();
  bookings.forEach((booking) => {
    if (booking.bookerKey && !seen.has(booking.bookerKey)) {
      seen.set(booking.bookerKey, { value: booking.bookerKey, label: booking.bookerName });
    }
  });
  return Array.from(seen.values()).sort(byLabel);
};

export const filterPlan = (
  bookings: OrganizationPlanBooking[],
  resourceKey: string,
  bookerKey: string
): OrganizationPlanBooking[] =>
  bookings.filter(
    (booking) =>
      (resourceKey === ALL || booking.resource?.key === resourceKey) &&
      (bookerKey === ALL || booking.bookerKey === bookerKey)
  );
